import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const horizon3Ai = defineConnector({
  identifier: "horizon3Ai",
  name: "Horizon3.ai",
  tagLine: "NodeZero penetration testing",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: ({ baseUrl }) =>
    exactUrlToRegex(baseUrl || "https://api.gateway.horizon3ai.com/v1"),
  auth: ({ configuration }) =>
    fetchTokenAuth({
      request: {
        url: {
          from: "literal",
          value:
            (configuration.baseUrl || "https://api.gateway.horizon3ai.com/v1")
              .trim()
              .replace(/\/+$/, "") + "/auth",
        },
        headers: [
          {
            name: "Content-Type",
            value: { from: "literal", value: "application/json" },
          },
        ],
        jsonBody: { key: { from: "secret", field: "apiKey" } },
      },
      extract: { from: "jsonPath", paths: ["token"] },
      apply: {
        kind: "header",
        name: "Authorization",
        scheme: "Bearer",
        extraHeaders: [
          {
            name: "Content-Type",
            value: { from: "literal", value: "application/json" },
          },
        ],
      },
    }),
  steps: [
    {
      title: "Set up Horizon3.ai",
      items: [
        {
          kind: "markdown",
          content:
            "In the Horizon3.ai portal, open **User → Account Settings** and generate an API key with read access. Use the API gateway for your region: **api.gateway.horizon3ai.com/v1** for the US or **api.gateway.horizon3ai.eu/v1** for the EU. See [API authentication](https://docs.horizon3.ai/api/getting_started/authenticate/).",
        },
      ],
    },
    {
      title: "Enter connection details",
      items: [
        {
          kind: "url",
          label: "API base URL",
          required: true,
          defaultValue: "https://api.gateway.horizon3ai.com/v1",
          target: {
            kind: "configuration",
            field: "baseUrl",
          },
          helperText:
            "Use the gateway region associated with the Horizon3.ai Portal account. The documented US API base is https://api.gateway.horizon3ai.com/v1. For an EU account, use https://api.gateway.horizon3ai.eu/v1 instead; the /auth and /graphql paths remain the same.",
        },
        {
          kind: "input",
          label: "Horizon3 API key",
          required: true,
          target: {
            kind: "secrets",
            field: "apiKey",
          },
          helperText:
            "Generate an API key in the Horizon3.ai Portal under User → Account Settings. Store it securely and use a read-capable permission level for read-only integrations.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) => ({
      url:
        (baseUrl || "https://api.gateway.horizon3ai.com/v1")
          .trim()
          .replace(/\/+$/, "") + "/graphql",
      ...{
        method: "POST",
        expectedBodyContains: ['"hello"', '"world!"'],
        headers: { "Content-Type": "application/json" },
        body: '{"query":"query HelloWorld { hello }"}',
      },
    }),
  },
});
